import { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Copy, Settings, Share2, Users2 } from "lucide-react";
import { useLeague } from "@/modules/leagues/hooks/useLeague";
import { leagueRepository } from "@/config/dataSource";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Badge } from "@/components/atoms/Badge";
import { ErrorState } from "@/components/organisms/ErrorState";
import { PageTransition } from "@/animations/PageTransition";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import { getCompetitionHero } from "@/config/competitionHeroes";
import { copyText, shareOrCopyText } from "@/utils/share";
import { toast } from "@/hooks/use-toast";

const TABS = [
  { key: "feed", label: "Feed" },
  { key: "palpites", label: "Palpites" },
  { key: "ranking", label: "Ranking" },
  { key: "estatisticas", label: "Stats" },
] as const;

const TAB_ROUTE: Record<(typeof TABS)[number]["key"], (leagueId: string) => string> = {
  feed: ROUTES.leagueFeed,
  palpites: ROUTES.leaguePredictions,
  ranking: ROUTES.leagueRanking,
  estatisticas: ROUTES.leagueStatistics,
};

/**
 * Shell de uma Liga — a Liga é o hub central da aplicação (ver addendum de
 * arquitetura "Liga como Hub Central"). Header com dados da liga + 3 abas
 * fixas (Feed/Palpites/Estatísticas). Configurações fica num botão separado
 * no header (só visível para admin/moderador), não é aba.
 */
export function LeagueShell() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { data: league, isLoading, isError } = useLeague(leagueId);

  // Registra "entrei nessa liga agora" pra Home ordenar "Minhas ligas" pela
  // mais recente acessada primeiro (item pedido) — dispara 1x por visita
  // (troca de leagueId), nunca bloqueia a navegação se falhar.
  useEffect(() => {
    if (leagueId) void leagueRepository.touchLastAccessed(leagueId);
  }, [leagueId]);

  async function handleCopyCode() {
    if (!league) return;
    const ok = await copyText(league.inviteCode);
    toast(ok ? { variant: "success", title: "Código copiado" } : { variant: "destructive", title: "Não foi possível copiar" });
  }

  async function handleShareCode() {
    if (!league) return;
    const result = await shareOrCopyText({
      title: "Palpiteiro",
      text: `Entra na minha liga "${league.nome}" no Palpiteiro! Código: ${league.inviteCode}`,
    });
    if (result === "copied") toast({ variant: "success", title: "Código copiado — cola pra convidar" });
    else if (result === "failed") toast({ variant: "destructive", title: "Não foi possível compartilhar" });
  }

  return (
    <div className="space-y-4 pb-4">
      <button
        onClick={() => navigate(ROUTES.home)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Minhas ligas
      </button>

      {isLoading && (
        <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      )}

      {isError && (
        <ErrorState
          title="Não foi possível carregar a liga"
          description="Verifique sua conexão e tente novamente."
          onRetry={() => window.location.reload()}
        />
      )}

      {league && !isLoading && (
        <>
          {(() => {
            const heroImage = getCompetitionHero(league.competitionId);
            return (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={cn(
                  "relative overflow-hidden rounded-2xl p-4 text-white shadow-glow-primary",
                  !heroImage && "bg-gradient-hero"
                )}
                style={
                  heroImage
                    ? { backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }
                    : undefined
                }
              >
                {heroImage ? (
                  // Overlay uniforme (não só embaixo) — aqui o texto e os
                  // badges ficam espalhados pela altura toda do card, não
                  // só na base, então precisa de contraste garantido em
                  // qualquer ponto da imagem (item 7/8 do sistema de Hero).
                  <div
                    className="absolute inset-0"
                    aria-hidden="true"
                    style={{ background: "linear-gradient(160deg, rgba(5,15,35,0.72) 0%, rgba(5,15,35,0.55) 100%)" }}
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-hero-radial" aria-hidden="true" />
                    {/* Bola decorativa — só no fallback sem Hero própria (item 23: a imagem real não leva decoração extra) */}
                    <svg
                      className="pointer-events-none absolute -right-4 -top-4 h-28 w-28 text-white/10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="m12 7 3.5 2.5-1.3 4.1H9.8L8.5 9.5 12 7Z" fill="currentColor" stroke="none" />
                      <path d="M12 2v2M21 12h-2M12 22v-2M3 12h2M18.5 5.5l-1.4 1.4M18.5 18.5l-1.4-1.4M5.5 18.5l1.4-1.4M5.5 5.5l1.4 1.4" />
                    </svg>
                  </>
                )}
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70 [text-shadow:0_1px_3px_rgb(0_0_0_/_0.5)]">
                      {league.competitionName}
                    </p>
                    <h1 className="font-display text-lg font-bold leading-tight [text-shadow:0_1px_4px_rgb(0_0_0_/_0.4)]">{league.nome}</h1>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <Badge variant="default" className="bg-white/15 text-white">
                        <Users2 className="h-3 w-3" /> {league.participantsCount}
                      </Badge>
                      <Badge variant="default" className="bg-white/15 text-white">
                        {league.currentRoundLabel}
                      </Badge>
                      <button
                        type="button"
                        onClick={handleCopyCode}
                        className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/25"
                        aria-label="Copiar código da liga"
                      >
                        <Copy className="h-3 w-3" /> {league.inviteCode}
                      </button>
                      <button
                        type="button"
                        onClick={handleShareCode}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/25"
                        aria-label="Compartilhar código da liga"
                      >
                        <Share2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  {/* Sempre visível pra qualquer membro (pedido do usuário) —
                      quem não é ADM vê uma versão só-leitura de alguns menus;
                      o controle de acesso real acontece dentro de cada página,
                      não aqui. */}
                  {(
                    <button
                      onClick={() => navigate(ROUTES.leagueSettings(league.id))}
                      aria-label="Configurações da liga"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm transition-colors hover:bg-white/25"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })()}

          <nav className="flex items-center gap-1 rounded-xl border border-border bg-card p-1" aria-label="Abas da liga">
            {TABS.map((tab) => (
              <NavLink
                key={tab.key}
                to={TAB_ROUTE[tab.key](league.id)}
                end
                className="relative flex-1"
              >
                {({ isActive }) => (
                  <span
                    className={cn(
                      "relative flex items-center justify-center rounded-lg py-2 text-xs font-semibold font-display transition-colors",
                      isActive ? "text-primary" : "text-muted-foreground"
                    )}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="league-tab-pill"
                        className="absolute inset-0 rounded-lg bg-primary-50 dark:bg-primary-900/40"
                        transition={{ type: "spring", stiffness: 420, damping: 32 }}
                      />
                    )}
                    <span className="relative">{tab.label}</span>
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <PageTransition>
            <Outlet context={{ league }} />
          </PageTransition>
        </>
      )}
    </div>
  );
}
